import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { filter, map, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AdminLayoutWrapperCSSClass } from '../../../core/constants-repository/app-style.constants';
import { ShiveDialogService } from '../../../core/services/controls/shive-dialog.service';
import { SharedClockHandlerService } from '../../../core/services/features/time-tracking/shared-clock-handler.service';
import { SharedTimerHandlerService } from '../../../core/services/features/time-tracking/shared-timer-handler.service';
import { SharedTrackerHandlerService } from '../../../core/services/features/time-tracking/shared-tracker-handler.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { ShiveRenderService } from '../../../core/services/rendering/shive-render.service';
import { TouchAndMouseHandlerService } from '../../../core/services/touch-and-mouse-handler.service';
import { DrawerService } from '../../../core/services/ui/drawer.service';
import { SidebarService } from '../../../core/services/ui/sidebar.service';
import { TimerStatus } from '../../../features/calendar/calendar-types';
import { SharedActiveTimeBlockService } from '../../../features/calendar/components/time-block/active/shared-active-time-block.service';
import { CssAttributes } from '../../../features/calendar/components/time-block/rendering/css-attributes';
import { LinkbarService } from '../template-parts/linkbar/linkbar.service';

@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  standalone: false,
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  // ViewChildren
  @ViewChild('contentWrapper') public contentWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('mainContent') public mainContent: ElementRef<HTMLDivElement>;
  @ViewChild('mainPanel') public mainPanel: ElementRef<HTMLDivElement>;
  @ViewChild('aside') public aside: ElementRef<HTMLElement>;

  // DI
  public sidebarService = inject(SidebarService);
  public readonly linkbarService = inject(LinkbarService);
  public readonly AdminLayoutWrapperCSSClass = AdminLayoutWrapperCSSClass;
  private readonly renderer = inject(ShiveRenderService);
  private readonly navigationService = inject(NavigationService); // Don't remove
  private readonly shiveDialogService = inject(ShiveDialogService); // Don't remove
  private readonly touchAndMouseHandlerService = inject(TouchAndMouseHandlerService);
  private readonly drawerService = inject(DrawerService);
  private readonly sharedTimerHandlerService = inject(SharedTimerHandlerService);
  private readonly sharedTrackerHandlerService = inject(SharedTrackerHandlerService);
  private readonly sharedClockHandlerService = inject(SharedClockHandlerService);

  // Misc
  private drawerExpanded = false;
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.subs.sink = this.drawerService.toggleDrawerContentWidth$.subscribe(() => {
      this.drawerExpanded = !this.drawerExpanded;
      if (this.drawerExpanded) {
        this.renderer.addClass(this.mainPanel.nativeElement, 'drawer-expanded');
        return;
      }

      this.renderer.removeClass(this.mainPanel.nativeElement, 'drawer-expanded');
    });

    this.initTimeBlockTracker();
    this.initTimeBlockTimer();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('window:mousemove', ['$event'])
  public onMouseMove(e: MouseEvent): void {
    this.touchAndMouseHandlerService.globalMouseMoved$.next(e);
    this.touchAndMouseHandlerService.currentMouseViewportPos = {
      x: e.clientX,
      y: e.clientY,
    };
  }

  @HostListener('window:mouseup', ['$event'])
  public onMouseUp(e: MouseEvent): void {
    this.touchAndMouseHandlerService.globalMouseReleased$.next(e);
  }

  public scroll(e: Event): void {
    this.touchAndMouseHandlerService.globalMouseScroll$.next((e.target as HTMLElement).scrollTop);
  }

  public mainPanelTranslationFinished($event: TransitionEvent): void {
    const containsExpand = this.mainPanel.nativeElement.classList.contains(
      'scale-content-area-expand',
    );
    const containsCompress = this.mainPanel.nativeElement.classList.contains(
      'scale-content-area-compress',
    );

    if ($event.propertyName === 'transform' && (containsExpand || containsCompress)) {
      if (!this.mainPanel.nativeElement.classList.contains('content-area-is-expanded')) {
        this.renderer.removeClass(this.mainPanel.nativeElement, 'scale-content-area-expand');
        this.renderer.addClass(this.mainPanel.nativeElement, 'content-area-is-expanded');
      } else {
        this.renderer.removeClass(this.mainPanel.nativeElement, 'content-area-is-expanded');
        this.renderer.removeClass(this.mainPanel.nativeElement, 'scale-content-area-compress');
      }
      this.sidebarService.sidebarEvents$.next($event);
      this.renderer.removeStyle(this.mainPanel.nativeElement, CssAttributes.transform);
    }
  }

  private initTimeBlockTracker(): void {
    // If there is an active time block, change controls in sidebar.
    this.sharedTrackerHandlerService.registerTimeTracking();
    this.sharedTrackerHandlerService.trackerResponse$
      .pipe(
        filter(
          (r) =>
            this.sharedClockHandlerService.clockStartedAt === null &&
            r !== null &&
            r.timeBlockModel !== null,
        ),
        map((r) => r.timeBlockModel),
      )
      .subscribe((timeBlockModel) =>
        this.sharedClockHandlerService.startClock(timeBlockModel.start.getTime()),
      );
  }

  private initTimeBlockTimer(): void {
    this.sharedTimerHandlerService
      .activeTimerTimeBlockExists()
      .pipe(take(1))
      .subscribe((activeTimerTimeBlock) => {
        if (activeTimerTimeBlock) {
          this.sharedTimerHandlerService.timerStatus = TimerStatus.Start;
          this.sharedTimerHandlerService.timerActiveTBChanged$.next(activeTimerTimeBlock.id);
          this.sharedClockHandlerService.startClock(
            activeTimerTimeBlock.timeBlockModel.start.getTime(),
          );
          return;
        }

        this.sharedTimerHandlerService.timerStatus = TimerStatus.Stop;
        this.sharedTimerHandlerService.timerActiveTBChanged$.next(
          SharedActiveTimeBlockService.noActiveTimerTimeBlockId,
        );
      });
  }
}
